import { useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';

import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { invalidateTeamMemberWithRelatedQueries } from '@/modules/common/utils/query-invalidation';
import { revokeTeamMemberAccess } from '@/modules/team/api/team.api';
import { TeamMember } from '@/modules/team/types/team-member';

interface RevokeAccessTeamMemberModalProps {
  teamMember: TeamMember;
  onSuccess?: () => void;
  trigger?: React.ReactNode;
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

/**
 * Modal for revoking platform access from a team member.
 * Removes login access while preserving all data in the platform.
 */
export function RevokeAccessTeamMemberModal({
  teamMember,
  onSuccess,
  trigger,
  open,
  onOpen,
  onClose,
}: RevokeAccessTeamMemberModalProps) {
  const { t } = useTranslation(['authenticated.team', 'common']);
  const queryClient = useQueryClient();
  const { displayToastErrorFromApiWithFallback } = useApiError();

  const revokeAccessTeamMemberMutation = useMutation({
    mutationFn: (memberId: string) => revokeTeamMemberAccess(memberId),
    onSuccess: async () => {
      await invalidateTeamMemberWithRelatedQueries(queryClient, teamMember.id);
      onSuccess?.();
    },
    onError: error =>
      displayToastErrorFromApiWithFallback(error, t('authenticated.team:revokeAccess.revokeError')),
  });

  const [isModalOpen, setIsModalOpen] = useState(false);

  // Determine the actual modal open state (external or internal)
  const actualModalOpen = open !== undefined ? open : isModalOpen;

  const handleRevokeAccess = async (closeModal: () => void) => {
    try {
      await revokeAccessTeamMemberMutation.mutateAsync(teamMember.id);
      closeModal();
    } catch (error) {
      // Error handled by mutation, keep modal open for retry
    }
  };

  const messageContent = (
    <div className="space-y-4">
      <div>
        <Trans
          i18nKey="authenticated.team:revokeAccess.revokeAccessTeamMemberMessage"
          values={{ name: teamMember.name }}
          components={{ strong: <strong /> }}
        />
      </div>
      <div className="space-y-2">
        <p className="font-semibold">{t('authenticated.team:revokeAccess.whatHappens')}</p>
        <ul className="ml-2 list-inside list-disc space-y-1">
          <li>{t('authenticated.team:revokeAccess.accessWillBeRevoked')}</li>
          <li>{t('authenticated.team:revokeAccess.dataWillBePreserved')}</li>
          <li>{t('authenticated.team:revokeAccess.statusWillChange')}</li>
          <li>{t('authenticated.team:revokeAccess.canBeReinvited')}</li>
        </ul>
      </div>
    </div>
  );

  return (
    <ConfirmationModal
      trigger={trigger}
      title={t('authenticated.team:revokeAccess.revokeAccessTeamMember')}
      message={messageContent}
      confirmText={
        revokeAccessTeamMemberMutation.isPending
          ? t('authenticated.team:revokeAccess.revoking')
          : t('authenticated.team:revokeAccess.revokeAccessTeamMember')
      }
      confirmVariant="destructive"
      onConfirm={handleRevokeAccess}
      isLoading={revokeAccessTeamMemberMutation.isPending}
      open={actualModalOpen}
      onOpen={onOpen || (() => setIsModalOpen(true))}
      onClose={
        onClose ||
        (() => {
          setIsModalOpen(false);
        })
      }
    />
  );
}
